import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Check, Zap, Star, Crown, Infinity, X, Loader2, ChevronRight, ShieldAlert, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';

const MONTHLY_PRICES = { standard: 3000, pro: 7500, gold: 17000, ultra: 30000 };

const PLANS = [
  {
    id: 'standard', name: 'Standard', credits: '30 usos/mes', icon: Zap,
    features: [
      '30 créditos por mes', 
      '1 archivo/URL de contexto', 
      'Planificación Simple (40 min)', 
      'Descarga en PDF y Biblioteca'
    ],
    accent: 'blue', btnClass: 'bg-blue-600 hover:bg-blue-700', cardClass: 'border-slate-700 bg-slate-800/50',
  },
  {
    id: 'pro', name: 'Pro', credits: '60 usos/mes', icon: Star, popular: true,
    features: [
      '60 créditos por mes', 
      'Clases Dobles (80 min) y secuencias cortas', 
      'Hasta 3 archivos en simultáneo', 
      'Todo lo incluido en el plan Standard'
    ],
    accent: 'violet', btnClass: 'bg-violet-600 hover:bg-violet-700', cardClass: 'border-violet-500/40 bg-violet-900/10',
  },
  {
    id: 'gold', name: 'Gold', credits: '120 usos/mes', icon: Crown, gold: true,
    features: [
      '120 créditos por mes', 
      'Planificaciones Semanales (clases de 120-160 min)', 
      'Creación de Guiones de Video, Exámenes y Resúmenes', 
      'Motor de Recompensas Gamificadas'
    ],
    accent: 'amber', btnClass: 'bg-amber-600 hover:bg-amber-700', cardClass: 'border-amber-500/40 bg-amber-900/10',
  },
  {
    id: 'ultra', name: 'Ultra', credits: 'Ilimitado', icon: Infinity, ultra: true,
    features: [ 
      'Créditos de uso ILIMITADOS para TODO', 
      'Lectura y trabajo sobre tu Planificación Anual Viva',
      'Repositorio Digital: Organizá toda tu bibliografía por materias', 
      'Exportación de Presentaciones PowerPoint (.pptx)'
    ],
    accent: 'yellow', btnClass: 'bg-yellow-600 hover:bg-yellow-700', cardClass: 'border-yellow-500/40 bg-yellow-900/10',
  },
];

function formatARS(amount) {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(amount);
}

export default function PricingModal({ open, onClose, currentPlan }) {
  const [billing, setBilling] = useState('monthly');
  const [loadingPlan, setLoadingPlan] = useState(null);

  const getPrice = (planId) => {
    const monthly = MONTHLY_PRICES[planId];
    if (billing === 'annual') return Math.round(monthly * 12 * 0.75);
    return monthly;
  };

  const getOriginalAnnualPrice = (planId) => {
    return MONTHLY_PRICES[planId] * 12;
  };

  const getSavings = (planId) => {
    const original = getOriginalAnnualPrice(planId);
    const discounted = getPrice(planId);
    return original - discounted;
  };

  const handleSelect = async (plan) => {
    if (loadingPlan) return;
    setLoadingPlan(plan.id);
    try {
      const { data } = await api.post('/payments/checkout', {
        plan_id: plan.id,
        billing,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar el pago. Intentá de nuevo.');
      setLoadingPlan(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* [&>button]:hidden oculta la "X" genérica de la librería para que usemos la nuestra */}
      <DialogContent className="bg-slate-900 border-slate-700 max-w-5xl w-[95vw] h-[90vh] md:h-auto md:max-h-[90vh] p-0 overflow-hidden flex flex-col gap-0 shadow-2xl z-50 [&>button]:hidden">
        
        {/* Header Compactado */}
        <div className="p-5 md:p-6 border-b border-slate-800/80 relative text-center bg-gradient-to-b from-slate-900 to-slate-950 shrink-0">
          
          {/* Tu "X" personalizada y única */}
          <button 
            onClick={onClose} 
            className="hidden md:flex absolute top-4 right-4 p-2 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white transition-all shadow-sm border border-slate-700 z-50"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
          
          <p className="text-[10px] md:text-xs text-blue-400 uppercase tracking-[0.15em] font-bold mb-1.5">Actualizá tu plan</p>
          <DialogTitle className="text-2xl md:text-3xl font-black text-white mb-1.5 tracking-tight">Potenciá tu enseñanza</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs md:text-sm font-medium">
            Elegí el plan que mejor se adapta a tu ritmo docente
          </DialogDescription>

          <div className="flex flex-col items-center justify-center mt-5">
            <div className="inline-flex items-center p-1.5 bg-slate-950 border border-slate-800 rounded-2xl shadow-inner w-full md:w-auto">
              <button
                onClick={() => setBilling('monthly')}
                className={`flex-1 md:px-8 py-2.5 rounded-xl text-xs md:text-sm font-bold transition-all ${
                  billing === 'monthly' ? 'bg-slate-800 text-white shadow-md' : 'text-slate-500 hover:text-white'
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setBilling('annual')}
                className={`flex-1 md:px-8 py-2.5 rounded-xl text-xs md:text-sm font-bold transition-all flex items-center justify-center gap-1 md:gap-2 ${
                  billing === 'annual' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-500 hover:text-emerald-400'
                }`}
              >
                Anual
                <span className={`${billing === 'annual' ? 'bg-white/20' : 'bg-emerald-500/20'} px-1.5 md:px-2 py-0.5 rounded text-[9px] md:text-[10px] uppercase tracking-wider`}>-25%</span>
              </button>
            </div>
            {billing === 'annual' && (
               <p className="text-emerald-400 text-[10px] md:text-xs font-bold mt-2.5 animate-fade-in-up uppercase tracking-widest">
                 ✓ Bonificación por pago anual activada
               </p>
            )}
          </div>
        </div>

        {/* Plans grid - Flex-1 y overflow-y-auto habilitan el scroll interno */}
        <div className="p-4 md:p-6 pt-8 md:pt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 overflow-y-auto flex-1 custom-scrollbar bg-[#030712]">
          {PLANS.map(plan => {
            const Icon = plan.icon;
            const isCurrent = currentPlan === plan.id;
            const isLoading = loadingPlan === plan.id;
            const price = getPrice(plan.id);
            const originalPrice = getOriginalAnnualPrice(plan.id);
            const savings = getSavings(plan.id);

            return (
              <div key={plan.id} data-testid={`plan-${plan.id}`} className={`relative rounded-3xl p-5 border transition-all flex flex-col bg-slate-900 shadow-xl ${plan.cardClass}`}>
                
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 flex justify-center w-full pointer-events-none">
                    <span className="bg-violet-600 text-white text-[10px] font-black px-4 py-1 md:px-5 md:py-1.5 rounded-full shadow-lg uppercase tracking-[0.15em] border border-violet-400/30 whitespace-nowrap">
                      Más Elegido
                    </span>
                  </div>
                )}

                {plan.ultra && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 flex justify-center w-full pointer-events-none">
                    <span className="bg-yellow-500 text-yellow-950 text-[10px] font-black px-4 py-1 md:px-5 md:py-1.5 rounded-full shadow-lg uppercase tracking-[0.15em] border border-yellow-300/50 whitespace-nowrap">
                      VIP
                    </span>
                  </div>
                )}

                {billing === 'annual' && (
                  <div className="absolute -right-2 -top-2 md:-right-3 md:-top-3 rotate-12 z-30 pointer-events-none">
                    <div className="bg-emerald-500 text-emerald-950 text-[9px] md:text-[10px] font-black px-2 py-1 md:px-3 md:py-1.5 rounded-xl shadow-xl border border-emerald-300 text-center leading-tight">
                      AHORRÁS<br/>{formatARS(savings)}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4 mt-1">
                  <div className={`w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center bg-slate-950 border border-slate-800 shadow-inner shrink-0`}>
                    <Icon size={20} strokeWidth={2} className={
                        plan.id === 'standard' ? 'text-blue-400' :
                        plan.id === 'pro' ? 'text-violet-400' :
                        plan.id === 'gold' ? 'text-amber-400' : 'text-yellow-400'
                    } />
                  </div>
                  <div>
                    <h3 className="text-white font-black text-lg md:text-xl leading-tight">{plan.name}</h3>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">{plan.credits}</p>
                  </div>
                </div>

                <div className="mb-5 bg-slate-950/50 rounded-xl p-3 md:p-4 border border-slate-800/50 text-center">
                  {billing === 'annual' && (
                    <div className="text-[10px] md:text-[11px] font-medium text-slate-500 line-through mb-1">
                      {formatARS(originalPrice)} / año
                    </div>
                  )}
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-2xl md:text-3xl font-black text-white tracking-tight">{formatARS(price)}</span>
                  </div>
                  <p className="text-[9px] md:text-[10px] text-emerald-400 font-bold mt-1.5 uppercase tracking-widest">
                    {billing === 'annual' ? 'Total a pagar hoy (12 meses)' : 'Facturado mensualmente'}
                  </p>
                </div>

                <ul className="space-y-3 mb-6 flex-1 px-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 md:gap-3 text-xs md:text-sm text-slate-300 font-medium">
                      <Check size={16} className={`shrink-0 ${
                        plan.id === 'standard' ? 'text-blue-400' :
                        plan.id === 'pro' ? 'text-violet-400' :
                        plan.id === 'gold' ? 'text-amber-400' : 'text-yellow-400'
                      }`} strokeWidth={3} />
                      <span className="leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`select-plan-${plan.id}`}
                  onClick={() => handleSelect(plan)}
                  disabled={isCurrent || !!loadingPlan}
                  className={`w-full py-3 md:py-3.5 rounded-xl text-xs md:text-sm font-black uppercase tracking-widest transition-all text-white flex items-center justify-center gap-2 shadow-lg mt-auto active:scale-[0.98] ${
                    isCurrent ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700 shadow-none'
                    : isLoading ? 'opacity-70 cursor-wait ' + plan.btnClass
                    : plan.btnClass
                  }`}
                >
                  {isLoading ? <><Loader2 size={16} className="animate-spin" /> Procesando</>
                    : isCurrent ? 'Plan Actual'
                    : <>Seleccionar <ChevronRight size={16} strokeWidth={3} /></>}
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="bg-slate-950 border-t border-slate-800/80 shrink-0">
          
          {/* Botón de Volver al Panel solo en móviles */}
          <div className="md:hidden p-4 border-b border-slate-800/50">
            <button 
              onClick={onClose}
              className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 uppercase tracking-widest text-xs border border-slate-700 transition-colors"
            >
              <ArrowLeft size={16} /> Volver al Panel
            </button>
          </div>

          <div className="p-3 text-center flex items-center justify-center gap-2">
            <ShieldAlert size={14} className="text-slate-500" />
            <p className="text-[9px] md:text-[11px] text-slate-500 font-bold uppercase tracking-widest">
              Transacción segura procesada por Mercado Pago.
            </p>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}
